import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, IUserModel, ModelType, ResourceType, VideoGenerationRequest, VideoClipStatus, ResourceStatus, VideoGenerationType, IVideoClipResource, VideoEditConfig, IEpisode, Resolution } from '@packages/global';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { promptHelperApi } from '../../../services/user/promptHelper.api';
import { imageUploadApi } from '../../../services/user/imageUpload.api';
import { videoFrameExtractor } from '../../../utils/videoFrameExtractor';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import { ReferenceMediaSection, ToolbarSection, ResourceList, VideoTypeTabs } from './components/VideoGeneration';
import { PromptInputSection, PromptEditModal } from './components';
import LipSyncForm from './LipSyncForm';

interface VideoGenerationFormProps {
  videoClip: IVideoClip;
  models: IUserModel[];
  modelsLoading: boolean;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  editConfig?: VideoEditConfig | null;
  onEditConfigChange?: (config: VideoEditConfig) => void;
  // 外部传入的资源状态（来自统一轮询）
  resources?: IVideoClipResource[];
  isPolling?: boolean;
  onTriggerPoll?: () => Promise<void>;
  episode?: IEpisode | null;
  // 画布拖拽注入参考图（可选，仅 ClipInfiniteCanvas 使用）
  externalAddImage?: { url: string; nonce: number };
  // 画布拖拽注入参考音频（可选，仅 ClipInfiniteCanvas 使用）
  externalAddAudio?: { url: string; nonce: number };
  onReferenceImageCleared?: (url: string) => void;
  onReferenceAudioCleared?: (url: string) => void;
  onCanvasSubmitSuccess?: () => void;
  initialParams?: Record<string, unknown>;
  canvasMode?: boolean;
  canvasActiveTab?: 'video' | 'lipSync';
}



export default function VideoGenerationForm({
  videoClip,
  models,
  modelsLoading,
  refreshVideoClip,
  editConfig,
  onEditConfigChange,
  resources: externalResources = [],
  isPolling = false,
  onTriggerPoll,
  episode,
  externalAddImage,
  externalAddAudio,
  onReferenceImageCleared,
  onReferenceAudioCleared,
  onCanvasSubmitSuccess,
  initialParams,
  canvasMode = false,
  canvasActiveTab
}: VideoGenerationFormProps) {
  const [activeTab, setActiveTab] = useState<'video' | 'lipSync'>(canvasActiveTab || 'video');
  const [formData, setFormData] = useState({
    mode: VideoGenerationType.KEYFRAME_TO_VIDEO,
    prompt: '',
    modelId: '',
    duration: 5,
    referenceImages: [] as string[],
    referenceAudio: '',
    isStoryboardGuide: false
  });

  // 为每个视频生成类型缓存参数状态
  const [modeParamsCache, setModeParamsCache] = useState<Record<VideoGenerationType, {
    prompt: string;
    modelId: string;
    duration: number;
    referenceImages: string[];
    referenceAudio: string;
    isStoryboardGuide: boolean;
  }>>({
    [VideoGenerationType.TEXT_TO_VIDEO]: {
      prompt: '',
      modelId: '',
      duration: 5,
      referenceImages: [],
      referenceAudio: '',
      isStoryboardGuide: false
    },
    [VideoGenerationType.IMAGE_TO_VIDEO]: {
      prompt: '',
      modelId: '',
      duration: 5,
      referenceImages: [],
      referenceAudio: '',
      isStoryboardGuide: false
    },
    [VideoGenerationType.KEYFRAME_TO_VIDEO]: {
      prompt: '',
      modelId: '',
      duration: 5,
      referenceImages: [],
      referenceAudio: '',
      isStoryboardGuide: false
    },
    [VideoGenerationType.MULTI_IMAGE_TO_VIDEO]: {
      prompt: '',
      modelId: '',
      duration: 5,
      referenceImages: [],
      referenceAudio: '',
      isStoryboardGuide: false
    }
  });

  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showAudioUpload, setShowAudioUpload] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [tempPrompt, setTempPrompt] = useState('');
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  // 过滤视频资源
  const filteredResources = externalResources
    .filter((r: any) => r.resourceType === ResourceType.VIDEO)
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // 检查是否有进行中的任务
  const isGenerating = externalResources.some((r: any) =>
    r.resourceType === ResourceType.VIDEO &&
    (r.status === ResourceStatus.PENDING || r.status === ResourceStatus.GENERATING)
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { alert, confirm } = useDialog();
  const { success, error } = useToast();
  const { t } = useTranslation(['editor', 'common']);

  // 根据生成模式过滤对应的模型
  const getModelTypeByMode = (mode: VideoGenerationType): ModelType | null => {
    switch (mode) {
      case VideoGenerationType.TEXT_TO_VIDEO:
        return ModelType.TEXT_TO_VIDEO;
      case VideoGenerationType.IMAGE_TO_VIDEO:
        return ModelType.IMAGE_TO_VIDEO;
      case VideoGenerationType.KEYFRAME_TO_VIDEO:
        return ModelType.KEYFRAME_TO_VIDEO;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO:
        return ModelType.MULTI_IMAGE_TO_VIDEO;
      default:
        return null;
    }
  };

  // 根据生成模式获取最大图片数量
  const getMaxImageCountByMode = (
    mode: VideoGenerationType,
    model?: IUserModel,
  ): number => {
    switch (mode) {
      case VideoGenerationType.TEXT_TO_VIDEO:
        return 0;
      case VideoGenerationType.IMAGE_TO_VIDEO:
        return 1;
      case VideoGenerationType.KEYFRAME_TO_VIDEO:
        return 2;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO:
        return model?.imageInputCount && model.imageInputCount > 0
          ? model.imageInputCount
          : 9;
      default:
        return 0;
    }
  };

  const modelType = getModelTypeByMode(formData.mode);
  const videoModels = modelType ? models.filter(model => model.modelType === modelType) : [];
  const selectedModel = models.find(m => m.id === formData.modelId);
  const maxImageCount = getMaxImageCountByMode(formData.mode, selectedModel);

  // 检查当前类型的所有模型是否都不支持音频
  const anyModelSupportsAudio = videoModels.some(m => m.audioInputCount != null && m.audioInputCount > 0);
  const audioSupported = selectedModel?.audioInputCount != null && selectedModel.audioInputCount > 0;
  // 音频时长小于3秒时视为不可用
  const isAudioTooShort = audioDuration !== null && audioDuration < 3;
  // 音频有效：模型支持音频且音频时长>=3秒
  const isAudioEffective = audioSupported && !isAudioTooShort;
  const hasAudioButNotSupported = Boolean(formData.referenceAudio && !isAudioEffective);

  // 表单验证逻辑
  const isFormValid = (() => {
    // 1. 必须选择模型
    if (!formData.modelId) return false;

    // 2. 所有模式都必须有提示词
    if (!formData.prompt.trim()) return false;

    // 3. 根据模式验证图片数量
    switch (formData.mode) {
      case VideoGenerationType.TEXT_TO_VIDEO:
        // 文生视频不需要图片
        break;
      case VideoGenerationType.IMAGE_TO_VIDEO:
        // 图生视频需要至少1张图片
        if (formData.referenceImages.length < 1) return false;
        break;
      case VideoGenerationType.KEYFRAME_TO_VIDEO:
        // 关键帧必须有2张图片
        if (formData.referenceImages.length !== 2) return false;
        break;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO:
        // 全模态参考允许1张起，最大数量由后台模型配置决定
        if (formData.referenceImages.length < 1 || formData.referenceImages.length > maxImageCount) return false;
        break;
    }

    // 4. 验证模型的 requiredInputs
    if (selectedModel?.requiredInputs && selectedModel.requiredInputs.length > 0) {
      for (const input of selectedModel.requiredInputs) {
        if (input === 'image' && formData.referenceImages.length === 0) return false;
        if (input === 'audio' && !formData.referenceAudio) return false;
        if (input === 'video') return false; // 暂不支持视频输入
      }
    }

    return true;
  })();

  const isDisabled = isGenerating; // 只有在真正生成时才禁用表单

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [externalResources.length]);

  // 记录上一次的 lastVideoParams，用于检测后台更新
  const prevLastVideoParamsRef = useRef<string | null | undefined>(undefined);
  // 记录上一次的 scriptAudioUrl，用于检测后台更新
  const prevScriptAudioUrlRef = useRef<string | undefined>(undefined);

  // 计算当前片段的默认表单值（不依赖 prev state）
  const getDefaultFormDataForMode = (mode: VideoGenerationType) => {
    const availableModels = models.filter(m => m.modelType === getModelTypeByMode(mode));
    const firstModel = availableModels.length > 0 ? availableModels[0] : undefined;
    const maxImages = getMaxImageCountByMode(mode, firstModel);
    const initialImages: string[] = [];
    let defaultIsStoryboardGuide = false;

    // 根据模式和片段数据初始化图片
    if (mode === VideoGenerationType.TEXT_TO_VIDEO) {
      // 文生视频不需要图片
    } else if (mode === VideoGenerationType.IMAGE_TO_VIDEO) {
      // 图生视频：优先使用分镜图，否则使用开始帧
      if (videoClip.storyboardImage) {
        initialImages.push(videoClip.storyboardImage);
        defaultIsStoryboardGuide = true;
      } else if (videoClip.startImage) {
        initialImages.push(videoClip.startImage);
      }
    } else if (mode === VideoGenerationType.KEYFRAME_TO_VIDEO) {
      // 关键帧：使用开始帧和结束帧
      if (videoClip.startImage) initialImages.push(videoClip.startImage);
      if (videoClip.endImage) initialImages.push(videoClip.endImage);
    } else if (mode === VideoGenerationType.MULTI_IMAGE_TO_VIDEO) {
      // 多图：尽可能多地使用可用图片
      if (videoClip.startImage) initialImages.push(videoClip.startImage);
      if (videoClip.endImage) initialImages.push(videoClip.endImage);
      if (videoClip.storyboardImage && initialImages.length < maxImages) {
        initialImages.push(videoClip.storyboardImage);
      }
    }

    const supportedDurations = firstModel?.supportedDurations ?? [];
    const defaultDuration = supportedDurations.length > 0 ? supportedDurations[0] : 5;

    return {
      prompt: '',
      modelId: firstModel?.id ?? '',
      duration: defaultDuration,
      referenceImages: initialImages,
      referenceAudio: videoClip.scriptAudioUrl || '',
      isStoryboardGuide: defaultIsStoryboardGuide
    };
  };

  // 计算初始模式
  const getInitialMode = () => {
    if (videoClip.storyboardImage) {
      return VideoGenerationType.IMAGE_TO_VIDEO;
    } else if (videoClip.startImage && videoClip.endImage) {
      return VideoGenerationType.KEYFRAME_TO_VIDEO;
    } else if (videoClip.startImage) {
      return VideoGenerationType.IMAGE_TO_VIDEO;
    }
    return VideoGenerationType.TEXT_TO_VIDEO;
  };

  // 切换片段时重置表单和缓存，优先使用最后一次生成参数
  useEffect(() => {
    // 初始化 ref
    prevLastVideoParamsRef.current = videoClip.lastVideoParams;
    // 切换 clip 时把 scriptAudioUrl ref 同步到当前 clip，避免下面的 audio sync effect 错误覆盖
    prevScriptAudioUrlRef.current = videoClip.scriptAudioUrl || '';

    // 初始化所有模式的缓存
    const newCache: typeof modeParamsCache = {
      [VideoGenerationType.TEXT_TO_VIDEO]: getDefaultFormDataForMode(VideoGenerationType.TEXT_TO_VIDEO),
      [VideoGenerationType.IMAGE_TO_VIDEO]: getDefaultFormDataForMode(VideoGenerationType.IMAGE_TO_VIDEO),
      [VideoGenerationType.KEYFRAME_TO_VIDEO]: getDefaultFormDataForMode(VideoGenerationType.KEYFRAME_TO_VIDEO),
      [VideoGenerationType.MULTI_IMAGE_TO_VIDEO]: getDefaultFormDataForMode(VideoGenerationType.MULTI_IMAGE_TO_VIDEO)
    };

    // 如果有最后一次生成参数，应用到对应模式的缓存
    if (videoClip.lastVideoParams) {
      try {
        const params = JSON.parse(videoClip.lastVideoParams);
        const mode = (params.mode || getInitialMode()) as VideoGenerationType;

        // 更新对应模式的缓存（需要临时添加mode字段用于mergeParamsIntoBase）
        if (newCache[mode]) {
          const tempBase = { mode, ...newCache[mode] };
          const merged = mergeParamsIntoBase(params, tempBase);
          // 移除mode字段，只保留缓存需要的字段
          const { mode: _, ...cacheParams } = merged;
          newCache[mode] = cacheParams;
        }
      } catch (e) {
        console.warn('Failed to parse last video params:', e);
      }
    }

    setModeParamsCache(newCache);

    // 设置初始模式和表单数据
    let initialMode: VideoGenerationType;
    try {
      initialMode = videoClip.lastVideoParams
        ? ((JSON.parse(videoClip.lastVideoParams).mode || getInitialMode()) as VideoGenerationType)
        : getInitialMode();
    } catch (e) {
      initialMode = getInitialMode();
    }

    setFormData({
      mode: initialMode,
      ...newCache[initialMode]
    });
  }, [videoClip.id, models]);

  // 监听 lastVideoParams 的后台更新（如分镜图完成后自动生成的视频提示词）
  useEffect(() => {
    // 跳过初始化（由上面的 effect 处理）
    if (prevLastVideoParamsRef.current === undefined) return;
    // 跳过相同值
    if (videoClip.lastVideoParams === prevLastVideoParamsRef.current) return;

    prevLastVideoParamsRef.current = videoClip.lastVideoParams;

    if (videoClip.lastVideoParams) {
      try {
        const params = JSON.parse(videoClip.lastVideoParams);
        // 后台更新时基于当前 formData 合并（此时不存在片段切换问题）
        handleReuseParams(params);
        console.log('[VideoGenerationForm] lastVideoParams 已从后台更新，自动应用新参数');
      } catch (e) {
        console.warn('Failed to parse updated lastVideoParams:', e);
      }
    }
  }, [videoClip.lastVideoParams]);

  // 监听 scriptAudioUrl 后台变化（如 AudioTab 提取 BGM / 上传 / 生成完成后），同步到表单和所有模式缓存
  // 避免用户从 AudioTab 提取 BGM 后切回视频表单看不到参考音频的问题
  useEffect(() => {
    const newAudioUrl = videoClip.scriptAudioUrl || '';
    // 首次设置 ref，跳过初始化（由切片段 effect 处理）
    if (prevScriptAudioUrlRef.current === undefined) {
      prevScriptAudioUrlRef.current = newAudioUrl;
      return;
    }
    if (newAudioUrl === prevScriptAudioUrlRef.current) return;
    prevScriptAudioUrlRef.current = newAudioUrl;

    // 同步当前表单
    setFormData(prev =>
      prev.referenceAudio === newAudioUrl ? prev : { ...prev, referenceAudio: newAudioUrl }
    );

    // 同步所有模式的缓存，避免切换模式后丢失
    setModeParamsCache(prev => {
      const next = { ...prev };
      let changed = false;
      (Object.keys(next) as VideoGenerationType[]).forEach(mode => {
        if (next[mode].referenceAudio !== newAudioUrl) {
          next[mode] = { ...next[mode], referenceAudio: newAudioUrl };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [videoClip.scriptAudioUrl]);

  const handleReuseParams = (params: any) => {
    // 用户手动复用参数时，基于当前 formData（prev）合并
    setFormData(prev => mergeParamsIntoBase(params, prev));
  };

  const initialParamsAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialParams || initialParamsAppliedRef.current) return;
    initialParamsAppliedRef.current = true;
    handleReuseParams(initialParams);
  }, [initialParams]);

  /**
   * 将 params 合并到 base 上，base 可以是当前 formData（手动复用）或默认值（切换片段）
   * 这样切换片段时不会把旧片段的数据通过 prev 泄漏到新片段
   */
  const mergeParamsIntoBase = (params: any, base: typeof formData) => {
    // 处理参考图片/视频参数
    let images: string[] = base.referenceImages;

    // 优先使用非空数组
    if (Array.isArray(params.referenceImages) && params.referenceImages.length > 0) {
      images = params.referenceImages;
    } else if (Array.isArray(params.imageUrls) && params.imageUrls.length > 0) {
      images = params.imageUrls;
    } else if (Array.isArray(params.referenceImages)) {
      images = [];
    } else if (Array.isArray(params.imageUrls)) {
      images = [];
    }

    // 优先保留显式模式；仅兼容没有 mode 的旧参数时按图片数量推断。
    let mode = params.mode || base.mode;
    if (!params.mode) {
      if (images.length === 0) {
        mode = VideoGenerationType.TEXT_TO_VIDEO;
      } else if (images.length === 1) {
        mode = VideoGenerationType.IMAGE_TO_VIDEO;
      } else if (images.length === 2) {
        mode = VideoGenerationType.KEYFRAME_TO_VIDEO;
      } else if (images.length > 2) {
        mode = VideoGenerationType.MULTI_IMAGE_TO_VIDEO;
      }
    }

    // 根据新模式获取可用模型
    const availableModels = models.filter(m => m.modelType === getModelTypeByMode(mode));
    const modelId = availableModels.some(m => m.id === params.modelId)
      ? params.modelId
      : (availableModels.length > 0 ? availableModels[0].id : base.modelId);

    // 根据新模型的 supportedDurations 校验/修正 duration
    const resolvedModel = availableModels.find(m => m.id === modelId) ?? availableModels[0];
    const supportedDurations = resolvedModel?.supportedDurations ?? [];
    const rawDuration = params.duration ?? base.duration;
    const duration = supportedDurations.length > 0
      ? (supportedDurations.includes(rawDuration) ? rawDuration : supportedDurations[0])
      : rawDuration;

    return {
      ...base,
      mode,
      prompt: params.prompt || base.prompt,
      modelId,
      duration,
      referenceImages: images,
      referenceAudio: params.referenceAudio || base.referenceAudio,
      isStoryboardGuide: params.isStoryboardGuide || false
    };
  };

  /**
   * 切换片段时使用：基于当前片段的默认值合并 params，避免旧片段数据泄漏
   */
  const applyParamsToForm = (params: any, defaults: typeof formData) => {
    setFormData(mergeParamsIntoBase(params, defaults));
  };

  // 获取视频时长
  const getVideoDuration = (videoUrl: string): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      video.onerror = () => {
        reject(new Error('Failed to load video'));
      };
      video.src = videoUrl;
    });
  };

  const buildReplacementClipTiming = (currentDuration: number, currentTrimStart: number, currentTrimEnd: number | undefined, newVideoDuration: number) => {
    const effectiveTrimEnd = currentTrimEnd ?? (currentTrimStart + currentDuration);
    const currentUsedDuration = Math.max(0, effectiveTrimEnd - currentTrimStart) || currentDuration;

    let nextDuration = currentUsedDuration;
    let nextTrimStart = currentTrimStart;
    let nextTrimEnd = effectiveTrimEnd;

    if (newVideoDuration >= effectiveTrimEnd) {
      nextDuration = currentUsedDuration;
      nextTrimStart = currentTrimStart;
      nextTrimEnd = effectiveTrimEnd;
    } else if (newVideoDuration >= currentUsedDuration) {
      nextDuration = currentUsedDuration;
      nextTrimStart = Math.max(0, Math.min(currentTrimStart, newVideoDuration - currentUsedDuration));
      nextTrimEnd = nextTrimStart + currentUsedDuration;
    } else {
      nextDuration = newVideoDuration;
      nextTrimStart = 0;
      nextTrimEnd = newVideoDuration;
    }

    return { nextDuration, nextTrimStart, nextTrimEnd };
  };

  // 获取音频时长
  const getAudioDuration = (audioUrl: string): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        resolve(audio.duration);
      };
      audio.onerror = () => {
        reject(new Error('Failed to load audio'));
      };
      audio.src = audioUrl;
    });
  };

  // 监听音频URL变化，获取音频时长
  useEffect(() => {
    if (formData.referenceAudio) {
      getAudioDuration(formData.referenceAudio)
        .then(duration => setAudioDuration(duration))
        .catch(() => setAudioDuration(null));
    } else {
      setAudioDuration(null);
    }
  }, [formData.referenceAudio]);

  // 应用视频到当前片段
  const handleApplyVideo = async (videoUrl: string) => {
    try {
      // 获取视频时长
      const videoDuration = await getVideoDuration(videoUrl);

      // 获取当前片段在editConfig中的信息
      const clipInfo = editConfig?.clips.find(c => c.clipId === videoClip.id);
      const currentDuration = clipInfo?.duration || videoClip.duration || 5;
      const currentTrimStart = clipInfo?.trimStart || 0;
      const currentTrimEnd = clipInfo?.trimEnd;
      const { nextDuration: newDuration, nextTrimStart, nextTrimEnd } = buildReplacementClipTiming(
        currentDuration,
        currentTrimStart,
        currentTrimEnd,
        videoDuration
      );

      // 如果当前 clip 没有 startImage（例如分镜生成时关闭了「生成图片」选项），
      // 用 Canvas（或 FFmpeg 兜底）从应用的视频里抽取第一帧作为 startImage，
      // 便于后续环节（缩略图/重新生成等）使用
      let extractedStartImageUrl: string | undefined;
      if (!videoClip.startImage) {
        console.log('[VideoGenerationForm] clip 无 startImage，开始抽取首帧', {
          clipId: videoClip.id,
          videoUrl,
        });
        try {
          const frameFile = await videoFrameExtractor.extractFrameAsFile(
            videoUrl,
            `clip-${videoClip.id}-first-frame.jpg`,
            0
          );
          console.log('[VideoGenerationForm] 首帧抽取成功，开始上传', {
            clipId: videoClip.id,
            size: frameFile.size,
          });
          const uploaded = await imageUploadApi.uploadImage(frameFile);
          extractedStartImageUrl = uploaded?.url;
          console.log('[VideoGenerationForm] 首帧上传成功', {
            clipId: videoClip.id,
            startImage: extractedStartImageUrl,
          });
        } catch (err: any) {
          // 抽帧/上传失败不阻断主流程，但提示用户，便于排查
          console.error('[VideoGenerationForm] 抽取视频首帧失败:', err);
          error(t('common:firstFrameExtractFailed', { defaultValue: '首帧提取失败：{{msg}}，请稍后在编辑器手动上传开始帧', msg: err?.message || '未知错误' }));
        }
      }

      // 更新 VideoClip（只更新videoUrl，不更新duration；可能附带回写 startImage）
      const updatePayload: any = {
        videoUrl,
        status: VideoClipStatus.COMPLETED,
      };
      if (extractedStartImageUrl) {
        updatePayload.startImage = extractedStartImageUrl;
      }
      await videoClipApi.updateVideoClip(videoClip.id, updatePayload);

      // 刷新 videoClip 数据
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }

      // 更新 editConfig 中的 ClipEditInfo
      if (editConfig && onEditConfigChange) {
        const clipIndex = editConfig.clips.findIndex(c => c.clipId === videoClip.id);
        if (clipIndex !== -1) {
          const oldClipDuration = editConfig.clips[clipIndex].duration;
          const durationDiff = newDuration - oldClipDuration;

          const updatedClips = editConfig.clips.map((clip, i) => {
            if (i === clipIndex) {
              // 当前片段：更新duration和trim值
              return {
                ...clip,
                duration: newDuration,
                trimStart: nextTrimStart,
                trimEnd: nextTrimEnd
              };
            }
            // 后续片段：调整startTime
            if (i > clipIndex) {
              return {
                ...clip,
                startTime: clip.startTime + durationDiff
              };
            }
            return clip;
          });

          // 更新总时长
          const totalDuration = editConfig.timeline.totalDuration + durationDiff;

          // 应用新配置
          onEditConfigChange({
            ...editConfig,
            clips: updatedClips,
            timeline: {
              ...editConfig.timeline,
              totalDuration
            }
          });
        }
      }

      success(t('common:videoApplied'));

      // 触发外部轮询刷新
      if (onTriggerPoll) {
        await onTriggerPoll();
      }
    } catch (err) {
      console.error('应用视频失败:', err);
      alert(t('common:videoApplyFailed'));
    }
  };

  const handleGenerate = async () => {
    // 表单验证已通过 isFormValid 和按钮禁用来控制，这里只做最后的安全检查
    if (!isFormValid) {
      return;
    }

    try {
      // 获取 Episode 的 resolution 和 aspectRatio
      const resolution = episode?.resolution || Resolution.HD_720P;
      const aspectRatio = episode?.videoAspectRatio;

      const params: VideoGenerationRequest = {
        mode: formData.mode,
        prompt: formData.prompt,
        modelId: formData.modelId,
        duration: formData.duration,
        resolution,
        aspectRatio,
        imageUrls: formData.referenceImages,
        // 只在图生视频模式时提交多宫格布局选项
        ...(formData.mode === VideoGenerationType.IMAGE_TO_VIDEO ? { isStoryboardGuide: formData.isStoryboardGuide } : {}),
        // 只有音频有效（模型支持且时长>=3秒）时才传递
        ...(formData.referenceAudio && isAudioEffective ? { referenceAudio: formData.referenceAudio } : {})
      };

      await videoClipResourceApi.generateVideo(videoClip.id, params);
      success(t('videoGenForm.taskSubmitted'));

      // 提交成功后重置提示词，保留参考图/音频以便微调
      setFormData(prev => ({ ...prev, prompt: '' }));
      onCanvasSubmitSuccess?.();

      // 触发外部轮询
      if (onTriggerPoll) {
        setTimeout(() => onTriggerPoll(), 1000);
      }
    } catch (err) {
      console.error('提交视频生成任务失败:', err);
      error(t('common:submitFailed'));
    }
  };

  const handleAutoGeneratePrompt = async () => {
    setIsGeneratingPrompt(true);
    try {
      // 优先使用时间线上的实际时长（用户可能已修改），而非原始 clip.duration
      const clipEditInfo = editConfig?.clips.find(c => c.clipId === videoClip.id);
      const effectiveDuration = clipEditInfo?.duration ?? videoClip.duration;
      const videoClipWithEffectiveDuration = { ...videoClip, duration: effectiveDuration };
      const result = await promptHelperApi.generateVideoPrompt(videoClipWithEffectiveDuration, formData.isStoryboardGuide);
      setFormData(prev => ({ ...prev, prompt: result.prompt }));

      // 保存生成的提示词到 lastVideoParams
      try {
        let lastParams: any = {};
        if (videoClip.lastVideoParams) {
          lastParams = JSON.parse(videoClip.lastVideoParams);
        }
        lastParams.prompt = result.prompt;
        lastParams.mode = VideoGenerationType.IMAGE_TO_VIDEO;
        await videoClipApi.updateVideoClip(videoClip.id, {
          lastVideoParams: JSON.stringify(lastParams)
        });

        // 刷新 videoClip 数据
        if (refreshVideoClip) {
          await refreshVideoClip(videoClip.id);
        }
      } catch (e) {
        console.warn('Failed to save prompt to lastVideoParams:', e);
      }

      success(t('videoGenForm.promptGenSuccess'));
    } catch (err) {
      console.error('Failed to generate prompt:', err);
      error(t('videoGenForm.promptGenFailed'));
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handleModeChange = (newMode: VideoGenerationType) => {
    // 1. 保存当前模式的参数到缓存
    setModeParamsCache(prev => ({
      ...prev,
      [formData.mode]: {
        prompt: formData.prompt,
        modelId: formData.modelId,
        duration: formData.duration,
        referenceImages: formData.referenceImages,
        referenceAudio: formData.referenceAudio,
        isStoryboardGuide: formData.isStoryboardGuide
      }
    }));

    // 2. 从缓存加载新模式的参数，如果缓存为空则使用默认值
    setFormData(prev => {
      const cachedParams = modeParamsCache[newMode];
      const availableModels = models.filter(m => m.modelType === getModelTypeByMode(newMode));

      // 如果缓存的模型ID不在可用模型列表中，使用第一个可用模型
      const modelId = availableModels.some(m => m.id === cachedParams.modelId)
        ? cachedParams.modelId
        : (availableModels.length > 0 ? availableModels[0].id : '');

      // 校验 duration 是否在新模型的 supportedDurations 内
      const resolvedModel = availableModels.find(m => m.id === modelId) ?? availableModels[0];
      const supportedDurations = resolvedModel?.supportedDurations ?? [];
      const duration = supportedDurations.length > 0
        ? (supportedDurations.includes(cachedParams.duration) ? cachedParams.duration : supportedDurations[0])
        : cachedParams.duration;

      return {
        mode: newMode,
        prompt: cachedParams.prompt,
        modelId,
        duration,
        referenceImages: cachedParams.referenceImages,
        referenceAudio: cachedParams.referenceAudio,
        isStoryboardGuide: newMode === VideoGenerationType.IMAGE_TO_VIDEO ? cachedParams.isStoryboardGuide : false
      };
    });
  };

  const addReferenceImage = (url: string) => {
    if (formData.referenceImages.length < maxImageCount) {
      setFormData(prev => ({
        ...prev,
        referenceImages: [...prev.referenceImages, url]
      }));
    }
    setShowImageUpload(false);
  };

  // 画布连线/快捷按钮注入参考图：nonce 变化即追加一次，避免重复注入
  const lastExternalNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!externalAddImage) return;
    if (lastExternalNonceRef.current === externalAddImage.nonce) return;
    lastExternalNonceRef.current = externalAddImage.nonce;
    const url = externalAddImage.url;
    if (!url) return;
    if (formData.referenceImages.includes(url)) return;
    if (formData.referenceImages.length >= maxImageCount) return;
    addReferenceImage(url);
  }, [externalAddImage]);

  // 画布连线注入参考音频：同步到当前视频模式和各模式缓存
  const lastExternalAudioNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!externalAddAudio) return;
    if (lastExternalAudioNonceRef.current === externalAddAudio.nonce) return;
    lastExternalAudioNonceRef.current = externalAddAudio.nonce;
    const url = externalAddAudio.url;
    if (!url) return;

    setFormData(prev => (
      prev.referenceAudio === url ? prev : { ...prev, referenceAudio: url }
    ));

    setModeParamsCache(prev => {
      const next = { ...prev };
      let changed = false;
      (Object.keys(next) as VideoGenerationType[]).forEach(mode => {
        if (next[mode].referenceAudio !== url) {
          next[mode] = { ...next[mode], referenceAudio: url };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [externalAddAudio]);

  const removeReferenceImage = (index: number) => {
    const removedUrl = formData.referenceImages[index];
    if (removedUrl) {
      onReferenceImageCleared?.(removedUrl);
    }
    setFormData(prev => ({
      ...prev,
      referenceImages: prev.referenceImages.filter((_, i) => i !== index)
    }));
  };

  const handleReferenceAudioChange = (url: string) => {
    if (!url && formData.referenceAudio) {
      onReferenceAudioCleared?.(formData.referenceAudio);
    }
    setFormData(prev => ({ ...prev, referenceAudio: url }));

    if (!url) {
      setModeParamsCache(prev => {
        const next = { ...prev };
        let changed = false;
        (Object.keys(next) as VideoGenerationType[]).forEach(mode => {
          if (next[mode].referenceAudio) {
            next[mode] = { ...next[mode], referenceAudio: '' };
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    const confirmed = await confirm(t('common:deleteResourceConfirm'), t('common:deleteResource'), 'danger');
    if (confirmed) {
      try {
        await videoClipResourceApi.deleteResource(resourceId);
        success(t('common:resourceDeleted'));
        if (onTriggerPoll) {
          await onTriggerPoll();
        }
      } catch (err) {
        console.error('删除资源失败:', err);
        error(t('common:deleteFailed'));
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {!canvasMode && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-gray-800 bg-gray-800/30">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('video')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeTab === 'video'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                }`}
            >
              {t('videoGenForm.videoClip')}
            </button>
            <button
              onClick={() => setActiveTab('lipSync')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeTab === 'lipSync'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                }`}
            >
              {t('videoGenForm.lipSync')}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'video' ? (
        <>
          {!canvasMode && (
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <ResourceList
                resources={externalResources}
                onApply={handleApplyVideo}
                onReuse={handleReuseParams}
                onDelete={handleDeleteResource}
              />
              <div ref={messagesEndRef} />
            </div>
          )}

          <div className="flex-shrink-0 border-t border-gray-800 bg-gray-800/30">
            <VideoTypeTabs
              value={formData.mode}
              onChange={handleModeChange}
              disabled={isDisabled}
            />

            <ReferenceMediaSection
              referenceImages={formData.referenceImages}
              referenceAudio={formData.referenceAudio}
              audioSupported={isAudioEffective}
              isDisabled={isDisabled}
              projectId={videoClip.projectId}
              episodeId={videoClip.episodeId}
              models={models}
              showImageUpload={showImageUpload}
              showAudioUpload={showAudioUpload}
              onImageUploadToggle={setShowImageUpload}
              onAudioUploadToggle={setShowAudioUpload}
              onAddImage={addReferenceImage}
              onRemoveImage={removeReferenceImage}
              onAudioChange={handleReferenceAudioChange}
              audioDuration={audioDuration}
              isAudioTooShort={isAudioTooShort}
              selectedModel={selectedModel}
              maxImageCount={maxImageCount}
              showAudioSection={anyModelSupportsAudio}
            />

            <PromptInputSection
              prompt={formData.prompt}
              isDisabled={isDisabled}
              isGeneratingPrompt={isGeneratingPrompt}
              isStoryboardGuide={formData.isStoryboardGuide}
              onPromptChange={(prompt) => setFormData(prev => ({ ...prev, prompt }))}
              onExpandClick={() => {
                setTempPrompt(formData.prompt);
                setShowPromptModal(true);
              }}
              onAutoGenerateClick={handleAutoGeneratePrompt}
              onStoryboardGuideChange={
                formData.mode === VideoGenerationType.IMAGE_TO_VIDEO
                  ? (value) => setFormData(prev => ({ ...prev, isStoryboardGuide: value }))
                  : undefined
              }
            />

            <ToolbarSection
              models={videoModels}
              modelId={formData.modelId}
              selectedModel={selectedModel}
              duration={formData.duration}
              resolution={episode?.resolution || Resolution.HD_720P}
              isDisabled={isDisabled}
              isGenerating={isGenerating}
              isSubmitDisabled={isDisabled || !isFormValid}
              modelsLoading={modelsLoading}
              hasAudioButNotSupported={hasAudioButNotSupported}
              onModelChange={(modelId) => {
                const newModel = videoModels.find(m => m.id === modelId);
                const supported = newModel?.supportedDurations ?? [];
                const newDuration = supported.length > 0
                  ? (supported.includes(formData.duration) ? formData.duration : supported[0])
                  : formData.duration;
                setFormData(prev => ({ ...prev, modelId, duration: newDuration }));
              }}
              onDurationChange={(duration) => setFormData(prev => ({ ...prev, duration }))}
              onSubmit={handleGenerate}
            />
          </div>

          {showPromptModal && (
            <PromptEditModal
              prompt={tempPrompt}
              onPromptChange={setTempPrompt}
              onClose={() => setShowPromptModal(false)}
              onConfirm={() => {
                setFormData(prev => ({ ...prev, prompt: tempPrompt }));
                setShowPromptModal(false);
              }}
            />
          )}
        </>
      ) : (
        <LipSyncForm
          videoClip={videoClip}
          models={models}
          modelsLoading={modelsLoading}
          refreshVideoClip={refreshVideoClip}
          resources={externalResources}
          isPolling={isPolling}
          onTriggerPoll={onTriggerPoll}
          onReferenceAudioCleared={onReferenceAudioCleared}
          onCanvasSubmitSuccess={onCanvasSubmitSuccess}
          initialParams={initialParams}
        />
      )}
    </div>
  );
}
